import { observer } from 'mobx-react';
import { FC, ReactElement, useEffect } from 'react';
import { BranchesModalBranches } from './containers/BranchesModalBranches/BranchesModalBranches';
import { BranchesModalHeader } from './containers/BranchesModalHeader/BranchesModalHeader';
import branchesModalStore from './store/branchesModalStore';
import { BUTTON_TITLE } from 'src/constants/main';
import { localeStore } from 'src/mobx/localesStore';
import { modalStore } from 'src/mobx/modalStore';
import { IBranch } from 'src/types/branches';
import { TBooleanInNumber, TTranslate } from 'src/types/main';
import Button from 'src/view/components/Controls/ButtonComponent/Button';
import CustomModalComponent from 'src/view/components/Controls/Modal/Modal';
import ModalBackground from 'src/view/components/Controls/Modal/ModalBackground/ModalBackground';
import ModalFooter from 'src/view/components/Controls/Modal/ModalFooter/ModalFooter';
import ModalHeader from 'src/view/components/Controls/Modal/ModalHeader/ModalHeader';
import Pagination from 'src/view/components/Controls/PaginationOld/PaginationOld';
import LoadingOverlay from 'src/view/components/LoadingOverlay/LoadingOverlay';
import ScrollableBlock from 'src/view/containers/ScrollableBlock/ScrollableBlock';

export enum BranchesModalProfile {
  DELIVERY = 'delivery',
  FRANCHISE = 'franchise',
  PREPARATION = 'preparation',
  HOURS = 'hours',
}

export interface IBranchesModalItem {
  id: number;
  name: TTranslate;
  is_busy?: boolean;
  profile_id?: number;
  profile_name?: string;
  is_delete?: TBooleanInNumber;
  status?: TBooleanInNumber;
  pos_service_id?: string | null;
}

/**
 * @param {IBranchesModalItem} selectedBranches - array of selected branches
 * @callback onSave
 * @callback externalValidateOnSelect - callback that is triggered when a branch is selected.
 * Serves for forwarding selection validation from the outside.
 * true - true - choice allowed, false - choice forbidden.
 * WARNING!!! Does not have multi-select button processing
 * @callback isFreezeOption - callback for "freezing" a branch. A frozen branch looks like it is unavailable and cannot be selected or unselected
 * @callback askAgainChecker - callback to check if "AskAgain" modal is needed
 * @callback askAgainHandler - callback for setting up and calling a "AskAgain" modal
 * WARNING!!! Both callbacks are required to work with a "AskAgain" modal
 * @param {string} headerTitle - custom modal title
 * @param {boolean} isMulti - multiple choice
 * @param {boolean} busy - show busy toggles
 * @param {boolean} saveWithoutBranches - allows saving without selected branches
 * @param {boolean} hideControls - hide "all" and "selected" crumbs, "Select all" and "Deselect all" buttons
 * @param {boolean} hideSelectAllButton - hide "Select all" button
 * @param {boolean} hideDeselectAllButton - hide "Deselect all" button
 * @param {BRANCHES_MODAL_FETCH_PARAMETER} fetchParamStatus - defines a request 'status' parameter. All === undefined, active === 1, inactive === 0
 * @param {BRANCHES_MODAL_FETCH_PARAMETER} fetchParamDelete - defines a request 'is_delete' parameter. All === undefined, active === 0, inactive === 1
 * @param {boolean} checkUnavailable - check the number of available branches. Inaccessible branches do not participate in calculations
 * @param {UNAVAILABLE_BRANCHES_FLAG} unavailableFlag - determines on what grounds to consider branch unavailable
 * @param {boolean} highlightUnavailable - highligh disabled(status === 0) or deleted(is_delete === 1) branches.
 * WARNING!!! Highlighted branches is not available for interaction
 * @param {BranchesModalProfile[]} profileNamesToShow - profile names to should be displayed
 * @see BranchesModalProfile
 */
const BranchesModal: FC = observer((): ReactElement => {
  useEffect(() => {
    branchesModalStore.initStore();
  }, []);

  useEffect(() => {
    const { askAgainHandler } = modalStore.branchesModal || {};

    if (askAgainHandler && branchesModalStore.askAgainBranches.length) {
      branchesModalStore.askAgainBranches.slice(0, 1).forEach((branch: IBranch) => askAgainHandler(branch, branchesModalStore.askAgainHandler));
    }
  }, [branchesModalStore.askAgainBranches]);

  return (
    <CustomModalComponent
      isOpen
      className={branchesModalStore.isMulti ? 'custom-modal' : 'custom-modal_single-choice'}
      onRequestClose={modalStore.closeBranchesModal}
      withCloseIcon
    >
      <ModalBackground>
        <ModalHeader title={branchesModalStore.modalTitle} />
        <BranchesModalHeader
          searchValue={branchesModalStore.searchValue}
          activeCrumb={branchesModalStore.activeCrumb}
          selectedBranches={branchesModalStore.selectedBranches}
          onSearch={branchesModalStore.onSearch}
          clickCrumb={branchesModalStore.changeCrumb}
          selectAll={branchesModalStore.selectAll}
          deselectAll={branchesModalStore.deselectAll}
          loading={branchesModalStore.loading}
          isMulti={branchesModalStore.isMulti}
          selectedItemsLength={branchesModalStore.selectedBranchesLength}
          hideMultiSelectButtons={modalStore.branchesModal?.hideControls}
          hideSelectAllButton={modalStore.branchesModal?.hideSelectAllButton}
          hideDeselectAllButton={modalStore.branchesModal?.hideDeselectAllButton}
        />

        <ScrollableBlock showScrollbar className="d-flex flex-1 mx-8" scrollClassName="full-height">
          <ul className="custom-modal__container mb-2">
            <BranchesModalBranches />
          </ul>
        </ScrollableBlock>
        {branchesModalStore.isNoBranchesForRender ? null : (
          <div className="mt-2">
            <Pagination pagination={branchesModalStore.pagination} onChange={branchesModalStore.fetchBranches} />
          </div>
        )}

        <ModalFooter>
          <Button
            title={localeStore.t(BUTTON_TITLE.SAVE)}
            onClick={branchesModalStore.onSave}
            disabled={branchesModalStore.isSubmitDisable}
            loading={branchesModalStore.loading}
            size="large"
          />
        </ModalFooter>

        <LoadingOverlay visible={branchesModalStore.loading} />
      </ModalBackground>
    </CustomModalComponent>
  );
});

export default BranchesModal;
